import { useCallback } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import type { ErrorMetadata } from '@cbhq/error-vitals-web';
import { ErrorBoundary } from '@cbhq/error-vitals-web';

import { BasicErrorFallback } from '../components/ErrorBoundary/BasicErrorFallback';
import { NavigationContentSpinner } from '../components/Navigation';
import { useOrganization } from '../contexts/OrganizationContext';
import { ActivityPage } from '../pages/Activity/pages/ActivityPage';
import { ROUTE_ACTIVITY } from '../pages/Activity/utils/constants';
import { ROUTE_TEAM } from '../pages/Team/utils/constants';
import { AppExperiments, useExperiments, useIsTreatment } from '../utils/experiments';

const ACTIVITY_ERROR_BOUNDARY_DEFAULT_METADATA: ErrorMetadata = {
  context: 'error-boundary',
  severity: 'error',
  isHandled: true,
};

function ActivityRoutes(): ReactElement {
  const { isLoaded } = useExperiments();
  const isWorkspaceAuditLogTreatment = useIsTreatment(AppExperiments.t0018_aac156e2);
  const shouldShowActivity = process.env.NODE_ENV === 'development' || isWorkspaceAuditLogTreatment;

  if (process.env.NODE_ENV !== 'development' && !isLoaded) {
    return <NavigationContentSpinner />;
  }

  if (!shouldShowActivity) {
    return <Navigate to={ROUTE_TEAM} replace />;
  }

  return (
    <Routes>
      <Route index element={<ActivityPage />} />
      <Route path="*" element={<Navigate to={ROUTE_ACTIVITY} replace />} />
    </Routes>
  );
}

export default function Activity(): ReactElement {
  const { selectedOrganization } = useOrganization();
  const handleCatch = useCallback((error: Error) => {
    logEvent('activity_management_error', {
      action: ActionType.measurement,
      componentType: ComponentType.page,
      error: error.message,
    });
  }, []);

  return (
    <VStack width="100%" height="100%">
      <ErrorBoundary
        // Remount the boundary when the org changes so a stale cached error
        // for the previous org doesn't keep the fallback stuck after switching.
        key={selectedOrganization?.organizationId}
        name="activity-boundary"
        defaultMetadata={ACTIVITY_ERROR_BOUNDARY_DEFAULT_METADATA}
        fallback={<BasicErrorFallback />}
        onCatch={handleCatch}
      >
        <ActivityRoutes />
      </ErrorBoundary>
    </VStack>
  );
}
